import React, { useState, useRef } from 'react';
import { X, Save, Palette, ImageIcon } from './Icons.jsx';
import { apiUpdateProfile } from '../api/ionos.js';

export function SettingsModal({ settings, user, onClose, onSave, onUploadLogo }) {
  const [formData, setFormData] = useState(settings);
  const [userFormData, setUserFormData] = useState({
    email: user?.email || '',
    password: '',
    confirmPassword: ''
  });

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);

  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleUserChange = (e) => {
    setUserFormData({ ...userFormData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    try {
      // 1. Save Company Settings
      await onSave(formData);

      // 2. Save User Profile if changed
      if (userFormData.password || userFormData.email !== user?.email) {
        if (userFormData.password && userFormData.password !== userFormData.confirmPassword) {
          alert('Passwörter stimmen nicht überein!');
          return;
        }
        await apiUpdateProfile({
          email: userFormData.email,
          password: userFormData.password
        });
        // Update local user data if email changed
        if (user && userFormData.email !== user.email) {
          const updated = { ...user, email: userFormData.email };
          localStorage.setItem('devsuite_user', JSON.stringify(updated));
        }
      }
      onClose();
    } catch (err) {
      console.error(err);
      alert('Fehler beim Speichern: ' + err.message);
    }
  };


  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-center items-center p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-[24px] sm:max-h-[90vh] rounded-none shadow-[0_8px_32px_rgba(0,0,0,0.12)] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-[20px] sm:text-[24px] font-bold text-slate-900 tracking-tight">Firmeneinstellungen</h2>
          <button onClick={onClose} type="button" className="p-2 min-h-[44px] min-w-[44px] bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 touch-manipulation flex items-center justify-center transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                <Palette size={16} className="mr-2" /> Design & Branding
              </h3>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Logo</label>
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-shrink-0 w-20 h-20 border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden">
                  {formData.logoUrl ? (
                    <img src={formData.logoUrl} alt="" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <ImageIcon size={32} className="text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <input
                    name="logoUrl"
                    value={formData.logoUrl || ''}
                    onChange={(e) => { setLogoError(null); handleChange(e); }}
                    placeholder="URL oder hochgeladenes Logo"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl text-sm"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        if (!onUploadLogo) {
                          setLogoError('Logo-Upload ist derzeit nicht verfügbar.');
                          return;
                        }
                        setLogoError(null);
                        setLogoUploading(true);
                        try {
                          const url = await onUploadLogo(file);
                          setFormData((prev) => ({ ...prev, logoUrl: url }));
                        } catch (err) {
                          setLogoError(err?.message || 'Upload fehlgeschlagen');
                        } finally {
                          setLogoUploading(false);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={logoUploading}
                      className="px-4 py-2.5 min-h-[44px] text-[15px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full disabled:opacity-50 touch-manipulation transition-colors"
                    >
                      {logoUploading ? 'Wird hochgeladen…' : 'Logo hochladen'}
                    </button>
                    <span className="text-xs text-slate-500">PNG, JPG, WebP oder GIF</span>
                  </div>
                  {logoError && <p className="text-xs text-red-600">{logoError}</p>}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">Logo-URL eintragen oder Datei hochladen (wird beim Speichern übernommen)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Primärfarbe</label>
              <div className="flex items-center gap-2 min-h-[44px]">
                <input
                  type="color"
                  name="primaryColor"
                  value={formData.primaryColor || '#2563eb'}
                  onChange={handleChange}
                  className="h-11 w-14 p-1 border border-slate-300 rounded-xl cursor-pointer touch-manipulation"
                />
                <span className="text-sm text-slate-600 font-mono">{formData.primaryColor}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">PDF-Dichte</label>
              <select
                name="pdfDensity"
                value={formData.pdfDensity || 'standard'}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              >
                <option value="standard">Standard</option>
                <option value="compact">Kompakt</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">PDF-Template</label>
              <select
                name="pdfTemplate"
                value={formData.pdfTemplate || 'classic'}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              >
                <option value="classic">Klassisch</option>
                <option value="clean">Clean</option>
              </select>
            </div>

            <div className="md:col-span-2 border-t border-slate-100 my-2" />

            <div className="md:col-span-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Allgemein</h3>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Firmenname</label>
              <input
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Gesellschafter / Inhaber</label>
              <input
                name="owner"
                value={formData.owner}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                placeholder="Bei GbR alle Partner angeben!"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amtsgericht / HRB (optional)</label>
              <input
                name="registry"
                value={formData.registry}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                placeholder="z.B. AG Berlin HRB 12345"
              />
            </div>

            <div className="md:col-span-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 mt-2">Adresse & Kontakt</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Straße & Hausnr.</label>
              <input
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">PLZ & Ort</label>
              <input
                name="zipCity"
                value={formData.zipCity}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail</label>
              <input
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                placeholder="info@nextgen-web.de"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
              <input
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                placeholder="+49 …"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
              <input
                name="website"
                value={formData.website || ''}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                placeholder="www.nextgen-web.de"
              />
            </div>

            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">E-Mail-Versand (SMTP)</h3>
              <p className="text-xs text-slate-500 mb-3">Pro Firma: SMTP-Server für den Versand (z. B. smtp.ionos.de).</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">SMTP-Host</label>
                  <input
                    name="smtpHost"
                    type="text"
                    value={formData.smtpHost || ''}
                    onChange={handleChange}
                    placeholder="z. B. smtp.ionos.de"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Port</label>
                  <input
                    name="smtpPort"
                    type="number"
                    value={formData.smtpPort ?? ''}
                    onChange={handleChange}
                    placeholder="587"
                    min={1}
                    max={65535}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Verschlüsselung</label>
                  <select
                    name="smtpEncryption"
                    value={formData.smtpEncryption ?? 'tls'}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  >
                    <option value="">Keine</option>
                    <option value="tls">TLS (Port 587)</option>
                    <option value="ssl">SSL (Port 465)</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Benutzer (E-Mail-Adresse)</label>
                  <input
                    name="smtpUser"
                    type="email"
                    value={formData.smtpUser || ''}
                    onChange={handleChange}
                    placeholder="rechnung@deine-domain.de"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Passwort</label>
                  <input
                    name="smtpPassword"
                    type="password"
                    value={formData.smtpPassword || ''}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="off"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
              </div>
            </div>

            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">IMAP (Gesendet-Kopie)</h3>
              <p className="text-xs text-slate-500 mb-3">Optional. Speichert nach erfolgreichem Versand eine Kopie im IMAP-Ordner „Gesendet“.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 flex items-center gap-3">
                  <input
                    id="imapEnabled"
                    name="imapEnabled"
                    type="checkbox"
                    checked={!!formData.imapEnabled}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="imapEnabled" className="text-sm font-medium text-slate-700">Kopie in „Gesendet“ speichern</label>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">IMAP-Host</label>
                  <input
                    name="imapHost"
                    type="text"
                    value={formData.imapHost || ''}
                    onChange={handleChange}
                    placeholder="z. B. imap.ionos.de"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">IMAP-Port</label>
                  <input
                    name="imapPort"
                    type="number"
                    value={formData.imapPort ?? 993}
                    onChange={handleChange}
                    placeholder="993"
                    min={1}
                    max={65535}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ordner „Gesendet“</label>
                  <input
                    name="imapSentFolder"
                    type="text"
                    value={formData.imapSentFolder || 'Sent'}
                    onChange={handleChange}
                    placeholder="Sent"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 mt-2">Finanzen & Steuern</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Steuernummer</label>
              <input
                name="taxId"
                value={formData.taxId}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">USt-ID (optional)</label>
              <input
                name="vatId"
                value={formData.vatId}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 p-3 border border-slate-200 rounded-xl">
                <input
                  type="checkbox"
                  name="isSmallBusiness"
                  checked={formData.isSmallBusiness === true || formData.isSmallBusiness === 1 || formData.isSmallBusiness === '1' || formData.isSmallBusiness === 'true'}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                <span className="text-sm text-slate-700">Kleinunternehmer nach §19 UStG (keine Umsatzsteuer ausweisen)</span>
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Bankname</label>
              <input
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">IBAN</label>
              <input
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">BIC</label>
              <input
                name="bic"
                value={formData.bic}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Gläubiger-ID (SEPA-QR, optional)</label>
              <input
                name="creditorId"
                value={formData.creditorId || ''}
                onChange={handleChange}
                placeholder="DE98ZZZ09999999999"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Rechnungsnummer-Präfix</label>
              <input
                name="invoiceNumberPrefix"
                value={formData.invoiceNumberPrefix ?? 'RE'}
                onChange={handleChange}
                placeholder="RE"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl max-w-[120px]"
              />
              <p className="text-xs text-slate-500 mt-1">z. B. RE → RE-2026-1</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">DATEV Erlöskonto</label>
              <input
                name="datevRevenueAccount"
                value={formData.datevRevenueAccount || '8400'}
                onChange={handleChange}
                placeholder="8400"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">DATEV Profilname</label>
              <input
                name="datevProfileName"
                value={formData.datevProfileName || 'Standard'}
                onChange={handleChange}
                placeholder="Steuerberater Müller"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">DATEV Debitorenkonto</label>
              <input
                name="datevDebitorAccount"
                value={formData.datevDebitorAccount || '10000'}
                onChange={handleChange}
                placeholder="10000"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">DATEV Erlöskonto 19%</label>
              <input
                name="datevRevenueAccount19"
                value={formData.datevRevenueAccount19 || '8400'}
                onChange={handleChange}
                placeholder="8400"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">DATEV Erlöskonto 7%</label>
              <input
                name="datevRevenueAccount7"
                value={formData.datevRevenueAccount7 || '8300'}
                onChange={handleChange}
                placeholder="8300"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">DATEV Erlöskonto 0%</label>
              <input
                name="datevRevenueAccount0"
                value={formData.datevRevenueAccount0 || '8125'}
                onChange={handleChange}
                placeholder="8125"
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">DATEV Trennzeichen</label>
              <select
                name="datevDelimiter"
                value={formData.datevDelimiter || ';'}
                onChange={handleChange}
                className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
              >
                <option value=";">Semikolon (;)</option>
                <option value=",">Komma (,)</option>
              </select>
            </div>

            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">Mahnwesen</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">1. Mahnung (€)</label>
                  <input
                    name="reminderFee1"
                    type="number"
                    step="0.01"
                    value={formData.reminderFee1 ?? 5}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">2. Mahnung (€)</label>
                  <input
                    name="reminderFee2"
                    type="number"
                    step="0.01"
                    value={formData.reminderFee2 ?? 10}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">3. Mahnung (€)</label>
                  <input
                    name="reminderFee3"
                    type="number"
                    step="0.01"
                    value={formData.reminderFee3 ?? 15}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Zahlungsziel (Tage)</label>
                  <input
                    name="reminderDays"
                    type="number"
                    step="1"
                    value={formData.reminderDays ?? 7}
                    onChange={handleChange}
                    placeholder="7"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Max. Mahnstufe</label>
                  <input
                    name="reminderMaxLevel"
                    type="number"
                    step="1"
                    min="1"
                    max="10"
                    value={formData.reminderMaxLevel ?? 3}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vertrag-Alarm (Tage)</label>
                  <input
                    name="contractAlertDays"
                    type="number"
                    step="1"
                    min="1"
                    max="365"
                    value={formData.contractAlertDays ?? 30}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <label className="flex items-center gap-2 p-3 border border-slate-200 rounded-xl">
                  <input
                    type="checkbox"
                    name="autoRunReminders"
                    checked={formData.autoRunReminders !== false}
                    onChange={handleChange}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-slate-700">Automatische Mahnstufen aktivieren</span>
                </label>
                <label className="flex items-center gap-2 p-3 border border-slate-200 rounded-xl">
                  <input
                    type="checkbox"
                    name="autoRunSubscriptions"
                    checked={formData.autoRunSubscriptions !== false}
                    onChange={handleChange}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-slate-700">Automatische Abo-Rechnungen aktivieren</span>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">Das Zahlungsziel in Tagen bestimmt, wann die nächste Mahnstufe ausgelöst wird.</p>
            </div>

            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">Abo-Automatik</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Zahlungsziel neue Abo-Rechnung (Tage)</label>
                  <input
                    name="subscriptionPaymentDays"
                    type="number"
                    step="1"
                    min="0"
                    value={formData.subscriptionPaymentDays ?? 14}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status neuer Abo-Rechnungen</label>
                  <select
                    name="subscriptionAutoStatus"
                    value={formData.subscriptionAutoStatus || 'draft'}
                    onChange={handleChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  >
                    <option value="draft">Entwurf</option>
                    <option value="sent">Gesendet</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2 mt-4">E-Mail-Vorlagen</h3>
              <p className="text-xs text-slate-500 mb-2">
                Verfügbare Platzhalter: {'{Dokumentnummer}'}, {'{Kunde}'}, {'{Betrag}'}, {'{Datum}'}, {'{Fälligkeitsdatum}'}, {'{GültigBis}'}, {'{Mahnstufe}'}, {'{Firma}'}
              </p>
              <label className="block text-sm font-medium text-slate-700 mb-1">Betreff Angebot</label>
              <input
                name="emailSubjectQuote"
                value={formData.emailSubjectQuote || ''}
                onChange={handleChange}
                placeholder="Angebot {Dokumentnummer} von {Firma}"
                className="w-full p-3 border border-slate-300 rounded-xl text-sm"
              />
              <label className="block text-sm font-medium text-slate-700 mb-1 mt-3">Text Angebot</label>
              <textarea
                name="emailTemplateQuote"
                value={formData.emailTemplateQuote || ''}
                onChange={handleChange}
                rows={3}
                placeholder="Hallo {Kunde}, anbei unser Angebot {Dokumentnummer} ..."
                className="w-full p-3 border border-slate-300 rounded-xl text-sm"
              />
              <label className="block text-sm font-medium text-slate-700 mb-1 mt-3">Betreff Rechnung</label>
              <input
                name="emailSubjectInvoice"
                value={formData.emailSubjectInvoice || ''}
                onChange={handleChange}
                placeholder="Rechnung {Dokumentnummer} von {Firma}"
                className="w-full p-3 border border-slate-300 rounded-xl text-sm"
              />
              <label className="block text-sm font-medium text-slate-700 mb-1 mt-3">Text Rechnung</label>
              <textarea
                name="emailTemplateInvoice"
                value={formData.emailTemplateInvoice || ''}
                onChange={handleChange}
                rows={3}
                placeholder="Hallo {Kunde}, anbei Ihre Rechnung {Dokumentnummer}. Bitte überweisen Sie {Betrag} bis {Fälligkeitsdatum}. …"
                className="w-full p-3 border border-slate-300 rounded-xl text-sm"
              />
              <label className="block text-sm font-medium text-slate-700 mb-1 mt-3">Betreff Mahnung</label>
              <input
                name="emailSubjectReminder"
                value={formData.emailSubjectReminder || ''}
                onChange={handleChange}
                placeholder="{Mahnstufe}. Mahnung {Dokumentnummer} von {Firma}"
                className="w-full p-3 border border-slate-300 rounded-xl text-sm"
              />
              <label className="block text-sm font-medium text-slate-700 mb-1 mt-3">Text Mahnung</label>
              <textarea
                name="emailTemplateReminder"
                value={formData.emailTemplateReminder || ''}
                onChange={handleChange}
                rows={2}
                placeholder="Leer = Standard-Text"
                className="w-full p-3 border border-slate-300 rounded-xl text-sm"
              />
            </div>
            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">Benutzerkonto & Sicherheit</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail (Login)</label>
                  <input
                    name="email"
                    type="email"
                    value={userFormData.email}
                    onChange={handleUserChange}
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl bg-slate-50"
                    readOnly
                    title="E-Mail kann aktuell nur vom Admin geändert werden."
                  />
                  <p className="text-xs text-slate-500 mt-1">E-Mail-Änderung bitte beim Admin anfragen.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Neues Passwort</label>
                  <input
                    name="password"
                    type="password"
                    value={userFormData.password}
                    onChange={handleUserChange}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Passwort bestätigen</label>
                  <input
                    name="confirmPassword"
                    type="password"
                    value={userFormData.confirmPassword}
                    onChange={handleUserChange}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="p-4 sm:p-6 border-t border-slate-100 flex flex-col-reverse sm:flex-row justify-end gap-3 bg-slate-50/50">
          <button onClick={onClose} type="button" className="px-5 py-2.5 min-h-[44px] text-[15px] font-semibold text-slate-600 hover:bg-slate-200 rounded-full touch-manipulation transition-colors">
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            type="button"
            className="px-6 py-2.5 min-h-[44px] bg-[#007AFF] text-white rounded-full hover:opacity-90 active:scale-[0.98] flex items-center justify-center touch-manipulation transition-all font-semibold text-[15px]"
          >
            <Save size={18} className="mr-2" /> Einstellungen speichern
          </button>
        </div>
      </div>
    </div>
  );
}
